import { Button, Flex, Tooltip } from '@aloft/toolkit-sui';

import { useAppraisifyNavigation } from '../../../hooks/validation/useAppraisifyNavigation.ts';
import { selectIsCommentaryLoaded } from '../../../redux/features/selectors/commentarySelectors/selectIsCommentaryLoaded.ts';
import { selectIsMockOrder, selectOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { getConfig } from '../../../utils/getConfig.ts';
import { useCertifyModal } from '../../CertifyModal/hooks/useCertifyModal.tsx';
import { useExportModal } from '../../ExportModal/useExportModal.tsx';
import { GuidedTourTooltip } from '../../GuidedTour/GuidedTourTooltip.tsx';
import { SUBNAV_HEIGHT } from '../consts.ts';
import { HeaderSubNavItem } from './HeaderSubNavItem.tsx';
import { APPRAISIFY_ONLY_ROUTES, SAAS_ONLY_ROUTES } from './constants.ts';
import { useActiveSubNav } from './useActiveSubNav.ts';

export const HeaderDesktopSubNav = () => {
  const { canCertify, getCertifyErrorText } = useAppraisifyNavigation();
  let subnav = useActiveSubNav();
  const isCommentaryLoaded = useAppSelector((state) => selectIsCommentaryLoaded(state)); // needed because Export Modal calls commentary selectors
  const { modal, openModal } = useExportModal(isCommentaryLoaded);
  const { certifyModal, openCertifyModal } = useCertifyModal();
  const order = useAppSelector(selectOrder);
  const isMock = useAppSelector(selectIsMockOrder);

  if (!subnav) return null;
  const certifyErrorText = getCertifyErrorText();

  const handleDocumentsClick = () => {
    if (!order.isInspectifyOrder || !order.orderNumber) return;

    const marketplaceBaseUrl = getConfig('INSPECTIFY_MARKETPLACE_BASE_URL');

    const url = `${marketplaceBaseUrl}/orders/${order.orderNumber}/result`;

    window.open(url, '_blank');
    window.focus();
  };

  // If the order is saas user, swap the order of analysis and market
  if (!order.isInspectifyOrder) {
    const marketIndex = subnav.findIndex((item) => item.route === 'market');
    const analysisIndex = subnav.findIndex((item) => item.route === 'analysis');

    if (marketIndex !== -1 && analysisIndex !== -1) {
      // Create a new array with market and analysis positions swapped
      subnav = [...subnav];
      const temp = subnav[marketIndex];
      // Only assign if both indices are valid and not undefined
      if (subnav[analysisIndex] !== undefined && subnav[marketIndex] !== undefined) {
        subnav[marketIndex] = subnav[analysisIndex];
        subnav[analysisIndex] = temp as (typeof subnav)[number];
      }
    }
  }

  // Override labels for inspectify orders
  if (order.isInspectifyOrder) {
    subnav = subnav.map((item) => {
      if (item.route === 'analysis') {
        return { ...item, label: 'Cost Approach' };
      }

      return item;
    });
  }

  return (
    <Flex
      position="absolute"
      alignItems="center"
      top="3rem"
      height={SUBNAV_HEIGHT}
      width="100vw"
      marginLeft="-3rem"
      paddingLeft="3rem"
      zIndex={9}
      backgroundColor="surface1"
      borderStyle="solid"
      borderTopColor="strokeDefault"
      borderBottomColor="strokeDefault"
      borderTopWidth="1px"
      role="ul"
    >
      {subnav.map((item) => {
        if (APPRAISIFY_ONLY_ROUTES.includes(item.route) && !order.isInspectifyOrder) {
          return null;
        }
        if (SAAS_ONLY_ROUTES.includes(item.route) && order.isInspectifyOrder) {
          return null;
        }
        return <HeaderSubNavItem key={item.route} item={item} />;
      })}
      {isCommentaryLoaded &&
        (order.isInspectifyOrder ? (
          <>
            <Tooltip label={certifyErrorText ?? 'Certify the report'}>
              <Button
                text={isMock ? 'Complete' : 'Certify'}
                disabled={!canCertify}
                onClick={openCertifyModal}
                size="sm"
                marginLeft="1rem"
                __testId="craftsman-certify-button"
              />
            </Tooltip>
            <Button
              text="Documents"
              size="sm"
              variant="secondary"
              onClick={handleDocumentsClick}
              marginLeft="1rem"
            />
          </>
        ) : (
          <GuidedTourTooltip
            text="Click 'Export' to export PDFs of your analysis or a file which can be imported directly into your form filling software."
            tourIndex={11}
            tourID="analysisTour"
          >
            <Button
              text="Export"
              onClick={openModal}
              size="sm"
              marginLeft="1rem"
              __testId="craftsman-export-button"
            />
          </GuidedTourTooltip>
        ))}
      {certifyModal}
      {modal}
    </Flex>
  );
};
